import React from "react";

import firebase from "firebase/compat/app";
import "firebase/compat/analytics";
import { Box } from "@mui/material";
import { useNavigate, Routes, Route, useParams } from "react-router-dom";
import { useRecoilValue } from "recoil";
import _ from "lodash";
import CoachData from "../../../data/CoachData";
import CoachTable from "./coaches/CoachTable";
import CoachSummary from "./coaches/CoachSummary";
import AddCoachDialog from "./coaches/AddCoachDialog";

import userDataState from "../../../atoms/userDataAtom";
import organizationIdSelector from "../../../atoms/organizationIdSelector";
import useEvents from "../../../hooks/useEvents";
import PageHeader from "../../../components/PageHeader";

const CoachesList = () => {
  const navigate = useNavigate();
  const organizationId = useRecoilValue(organizationIdSelector);

  const [coaches, setCoaches] = React.useState([]);
  const [showDisabled] = React.useState(true);
  const [editCoachOpen, setCoachEditOpen] = React.useState(false);

  const subscription = React.useRef(undefined);
  const userData = useRecoilValue(userDataState);

  React.useEffect(() => {
    firebase.analytics().logEvent("page_view", {
      page_title: "Coaches Page",
      page_location: "/coaches",
    });
  }, []);

  React.useEffect(() => {
    async function getCoaches() {
      if (subscription.current) {
        subscription.current();
      }

      subscription.current = await CoachData.getCoachesSubscription(
        (snapshot) => {
          if (showDisabled) {
            setCoaches(snapshot);
          } else {
            const filtered = _.filter(snapshot, { status: "active" });
            setCoaches(filtered);
          }
        },
      );
    }
    getCoaches();

    return () => {
      if (subscription.current) {
        subscription.current();
      }
    };
  }, [showDisabled]);

  return (
    <>
      <PageHeader 
        title="Coaches" 
        subtitle="Manage your coaching team and assign client responsibilities"
      />
      <Box
        sx={{
          display: "flex",
          flex: 1,
          flexDirection: "column",
          px: { xs: 2, sm: 3 },
        }}
      >
        <CoachTable
          coaches={coaches}
          onAddCoachClicked={() => {
            setCoachEditOpen(true);
          }}
          onCoachClicked={(coach) => {
            navigate(`/home/coaches/coach/${organizationId}/${coach.uid}`);
          }}
        />
        {editCoachOpen && (
          <AddCoachDialog
            open={editCoachOpen}
            userData={userData}
            onDialogClosed={(coach) => {
              if (coach) {
                CoachData.addCoach(coach);
              }
              setCoachEditOpen(false);
            }}
          />
        )}
      </Box>
    </>
  );
};

const CoachDetail = () => {
  const { coachId } = useParams();
  const navigate = useNavigate();
  const events = useEvents();
  const userData = useRecoilValue(userDataState);

  const [coach, setCoach] = React.useState(null);

  React.useEffect(() => {
    if (!coachId) return;

    const subscription = CoachData.getCoachSubscription(coachId, (snapshot) => {
      setCoach(snapshot);
    });

    return () => {
      if (subscription) {
        subscription();
      }
    };
  }, [coachId]);

  if (!coach) {
    return <Box>Loading coach...</Box>;
  }

  return (
    <Box sx={{ px: { xs: 2, sm: 3 } }}>
      <CoachSummary
        userData={userData}
        coach={coach}
        onBackClick={() => {
          events.logButtonClick({
            buttonName: "Back Button",
            pageName: "Coaches",
            tabName: coach.uid,
          });
          navigate("/home/coaches");
        }}
      />
    </Box>
  );
};

const CoachesPage = () => {
  const navigate = useNavigate();

  React.useEffect(() => {
    return () => {
      if (navigate.action === "POP") {
        navigate("/home");
      }
    };
  }, [navigate]);

  return (
    <Routes>
      <Route path="" element={<CoachesList />} />
      <Route path="coach/:orgId/:coachId" element={<CoachDetail />} />
    </Routes>
  );
};

export default CoachesPage;
